/**
 * group-members-menu.tsx — Nút "Thành viên (n) ⌄" ngay dưới tên nhóm.
 * Mở danh sách thành viên (avatar · tên · uid) theo mẫu: nút Đồng bộ kéo lại
 * từ Zalo, nút Xóa gỡ thành viên (cần quyền trưởng/phó nhóm trên Zalo).
 * Bấm vào avatar/tên một thành viên (có `accountId` — tài khoản Zalo cá nhân) →
 * mở `ContactProfileMenu` (Xem trang cá nhân/Nhắn tin riêng/Thêm bạn), cùng menu
 * đã có ở tên người gửi trong khung chat.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Users, ChevronDown, RefreshCw, MinusCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from '@/components/ui/dropdown-menu'
import { apiError } from '@/lib/api-client'
import { initials } from '@/lib/utils'
import {
  refreshGroupMembers, useRemoveGroupMember, type GroupMember,
} from '@/hooks/use-conversations'
import { ContactProfileMenu } from './contact-profile-menu'

export function GroupMembersMenu({ convId, members, accountId }: {
  convId: string
  members: GroupMember[]
  /** channelAccountId của tài khoản Zalo cá nhân đang xem — có thì bấm được avatar/tên mỗi thành viên (menu Zalo gốc). */
  accountId?: string
}) {
  const { t } = useTranslation('convContact')
  const qc = useQueryClient()
  const removeMember = useRemoveGroupMember(convId)
  const [syncing, setSyncing] = useState(false)

  async function handleSync() {
    setSyncing(true)
    try {
      await refreshGroupMembers(convId)
      await qc.invalidateQueries({ queryKey: ['conversation', convId, 'group-members'] })
      toast.success(t('groupMembers.syncedToast'))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSyncing(false)
    }
  }

  async function handleRemove(m: GroupMember) {
    if (!window.confirm(t('groupMembers.confirmRemove', { name: m.name }))) return
    try {
      await removeMember.mutateAsync({ memberId: m.uid })
      toast.success(t('groupMembers.removedToast', { name: m.name }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // shrink-0 + nowrap: ở đầu khung chat điện thoại dòng phụ rất hẹp — không được xuống hai dòng; nhãn kênh bên cạnh tự cắt …
          className="flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground hover:text-foreground"
        >
          <Users className="h-3.5 w-3.5" />
          {t('groupMembers.trigger', { count: members.length })}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            <Users className="h-4 w-4" /> {t('groupMembers.trigger', { count: members.length })}
          </span>
          <Button variant="outline" size="sm" onClick={() => void handleSync()} disabled={syncing}>
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> {t('groupMembers.sync')}
          </Button>
        </div>
        <div className="max-h-80 overflow-y-auto p-1.5">
          {members.length === 0 ? (
            <p className="p-4 text-center text-xs text-muted-foreground">
              {t('groupMembers.empty')}
            </p>
          ) : members.map((m) => {
            const row = (
              <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-0.5">
                <Avatar className="h-8 w-8">
                  {m.avatarUrl && <AvatarImage src={m.avatarUrl} alt={m.name} />}
                  <AvatarFallback>{initials(m.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-left">
                  <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                    {m.name}
                    {m.isAdmin && <Badge variant="secondary" className="px-1.5 text-[10px]">{t('groupMembers.admin')}</Badge>}
                  </p>
                  <p className="truncate text-[11px] text-primary">zlw{m.uid}</p>
                </div>
              </div>
            )
            return (
              <div key={m.uid} className="flex items-center gap-1 rounded-md px-1 py-1 hover:bg-muted">
                {accountId ? (
                  <ContactProfileMenu accountId={accountId} uid={m.uid} name={m.name} avatarUrl={m.avatarUrl}>
                    <button type="button" className="min-w-0 flex-1 rounded-md hover:bg-muted/60">{row}</button>
                  </ContactProfileMenu>
                ) : row}
                <Button
                  variant="outline" size="sm"
                  disabled={removeMember.isPending}
                  onClick={() => void handleRemove(m)}
                >
                  <MinusCircle className="h-3.5 w-3.5" /> {t('groupMembers.remove')}
                </Button>
              </div>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
